import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { AddressMedia, PaginatedResponse } from "@/api.types";
import { useSession } from "@/services/auth0/session";

// Fetch all address medias with pagination
export function useAddressMedias(page = 1, limit = 50) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["address-medias", page, limit],
    queryFn: async () => {
      const response = await axios.get<PaginatedResponse<AddressMedia>>(
        `${import.meta.env.VITE_API_URL}/api/v1/address-medias`,
        {
          params: { page, limit },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!token,
  });
}

// Fetch a single address media by ID
export function useAddressMedia(id: string) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["address-media", id],
    queryFn: async () => {
      const response = await axios.get<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v1/address-media/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!id && !!token,
  });
}

// Delete an address media
export function useDeleteAddressMedia() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v1/address-media/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["address-medias"] });
    },
  });
}

// Delete a media item from an address media
export function useDeleteMediaItem() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      addressMediaId,
      mediaItemId,
    }: {
      addressMediaId: string;
      mediaItemId: string;
    }) => {
      const response = await axios.delete<AddressMedia>(
        `${import.meta.env.VITE_API_URL}/api/v1/address-media/${addressMediaId}/media/${mediaItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["address-medias"] });
      void queryClient.invalidateQueries({ queryKey: ["address-media"] });
    },
  });
}
